<template>
    <wrapper-features-templates-operations
    :hideContentWrapper="!showLoader && !applyErrorState && !getAccountsBalances.length"
    :customBreadcrumbs="customBreadcrumbs"
    :showLoader="showLoader"
    :showOverlayLoader="showOverlayLoader"
    :applyErrorState="applyErrorState && !showLoader"
    :showStepper="activeStep <= stepperParams.length && !applyErrorState && !showLoader && !!getAccountsBalances.length"
    :stepperParams="stepperParams"
    :activeStep="activeStep"
    :showFooter="activeStep < 3 && !showLoader && !applyErrorState && !!getAccountsBalances.length"
    :footerButtonsParams="footerButtonsParams.filter(obj => obj.step === activeStep)"
    :tokenTitleAppend="t('payments.requests.baiPaga.pay.tokenTitleAppend')"
    :feedbackOperationData="feedbackOperationData"
    :scrollValue="scrollValue"
    @challengeSuccessHandler="successHandler"
    @challengeErrorHandler="errorHandler($event, payPendingBaiPagaRequest)"
    @backOrCloseClicked="navigateBack()"
    @clearScrollToValue="scrollValue = null">
        <!-- *********************************************
        *												 *
        *                     Content                    *
        *												 *
        ********************************************** -->
        <component
        v-if="!applyErrorState && !showLoader && !!getAccountsBalances.length"
        :is="getActiveStepComponent"
        :isMobile="getMobileAppState"
        :accountsBalances="getAccountsBalances"
        :clearFormErrors="clearFormErrors"
        :formData="formData"
        :mandatoryList="mandatoryList"
        :responseData="responseData"
        :selectedPayment="selectedPayment"
        :showMandatoryErrors="showMandatoryErrors"
        @formDataChange="formDataChangeHandler"
        @formErrorsCleared="clearFormErrors = false"
        @resetShowMandatoryErrorsProp="showMandatoryErrors = false"
        @scrollTo="scrollValue = $event"/>
        <!-- Empty-state -->
        <template #empty-state-slot>
            <transition name="common-enter-animation" mode="out-in" appear>
                <empty-state-generic
                v-if="!applyErrorState && !showLoader && !getAccountsBalances.length"
                illustration="emptyState/payments"
                :title="t('accountsEmptyStateTitle')"
                :description="t('accountsEmptyStateDescription')"/>
            </transition>
        </template>
    </wrapper-features-templates-operations>
    <!-- Feedback Toast -->
    <feedback-toast
	v-if="showFeedbackToast"
	:toastType="feedbackToastData.toastType"
	:feedbackText="feedbackToastData.text"
	:buttonsParams="feedbackToastData.buttons"
	@hideMe="showFeedbackToast = false"/>
</template>

<script setup>
import WrapperFeaturesTemplatesOperations from '@/components/wrappers/featuresTemplates/Operations'
import EmptyStateGeneric from '@/components/generic/emptyStates/Generic'
import FeedbackToast from '@/components/generic/toasts/Generic'

/* Vue */
import { ref, computed, defineAsyncComponent, onBeforeUnmount } from 'vue'

/* Assets & Helpers */
import { atLeastOneIsEmpty, clearObject } from '@/assets/scripts/objects'
import { routeList } from '@/router/helpers/routes'
import { navigateByName } from '@/router/helpers/navigation'
import { navigateBack } from '@/router/helpers/navigation'
import { errorObjectConstructor, emptyObjectConstructor } from '@/assets/scripts/toastsHelpers'

/* Store */
import { mapActions, mapGetters } from '@/store/helpers/mappers'
import { moduleMapper } from '@/store/helpers/modules.map'
import { createDownloadMobilePaymentsReceiptPdf } from '@/network/api/mobilePayments.api.js'

/* i18n */
import { useI18n } from 'vue-i18n'
const { t } = useI18n()

/* Emmitters */
const emit = defineEmits(['backOrCloseClicked'])

/* props received
@params { Object } - data received from the chosen request to pay
*/
const props = defineProps({
    params: Object
})

/* mapActions & mapGetters */
const { requestAccountsBalances, payPendingBaiPaga, clearPayFeatureStates } = mapActions(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getAccountsBalances } = mapGetters(moduleMapper.PAYMENTS_REQUESTS_BAI_PAGA)
const { getMobileAppState } = mapGetters()

/* Show Data */
const showLoader = ref(false)
const showOverlayLoader = ref(false)

/* Data */
const applyErrorState = ref(false)
const selectedPayment = ref(props.params)

const customBreadcrumbs = ref({
    currentArea: t('payments.requests.baiPaga.pay.customBreadcrumbs.currentArea'),
    parentMenus: [{
        id: 1,
        text: t('payments.requests.baiPaga.pay.customBreadcrumbs.payments')
    },
    {
        id: 2,
        text: t('payments.requests.baiPaga.pay.customBreadcrumbs.requests'),
        link: routeList.PAYMENTS_REQUESTS_BAI_PAGA_PATH
    }]
})

/*************************************************
*                                                *
*                    Steps Data                  *
*                                                *
*************************************************/
const activeStep = ref(1)
const stepperParams = ref([
    { description: t('payments.requests.baiPaga.pay.stepper.data') },
    { description: t('payments.requests.baiPaga.pay.stepper.validation') }
])

const getActiveStepComponent = computed(() => {
    const stepNumber = activeStep.value
    return defineAsyncComponent(() => import('@/components/features/payments/requests/baiPagaPay/Step' + stepNumber))
})

/*************************************************
*                                                *
*                Form Logic Data                 *
*                                                *
*************************************************/
const scrollValue = ref(null)
const showMandatoryErrors = ref(false)
const clearFormErrors = ref(false)

const formData = ref({
    originAccount: '',
    currency: selectedPayment.value.currency ? selectedPayment.value.currency : '',
    amount: selectedPayment.value.amount ? selectedPayment.value.amount : '',
    estimatedAmount: selectedPayment.value.estimatedAmount ? selectedPayment.value.estimatedAmount : '',
    maxAmount: selectedPayment.value.maxAmount ? selectedPayment.value.maxAmount : ''
})

const mandatoryList = ref([
    'originAccount'
])

const formDataChangeHandler = ({ field, inputValue }) => {
    formData.value[field] = inputValue
}

/*************************************************
*                                                *
*               Footer/Buttons Logic             *
*                                                *
*************************************************/
/* Footer Buttons Data */
const footerButtonsParams = ref([
    {
        label: t('payments.requests.baiPaga.pay.footerButtons.cancel'),
        callbackFunction: () => cancelHandler(),
        buttonPosition: 'left',
        step: 1
    }, {
        label: t('payments.requests.baiPaga.pay.footerButtons.forward'),
        callbackFunction: () => validateInputs(),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'arrow-right-full',
        iconPosition: 'right',
        step: 1
    }, {
        label: t('payments.requests.baiPaga.pay.footerButtons.back'),
        callbackFunction: () => { activeStep.value = 1 },
        buttonPosition: !getMobileAppState.value ? 'right' : 'left',
        icon: 'arrow-left-full',
        step: 2
    }, {
        label: t('payments.requests.baiPaga.pay.footerButtons.validate'),
        callbackFunction: () => payPendingOrder(),
        buttonType: 'primary',
        buttonPosition: 'right',
        icon: 'check-line',
        iconPosition: 'right',
        step: 2
    }
])

const cancelHandler = () => {
    clearFormErrors.value = true
    clearObject(formData.value)

    navigateByName(routeList.PAYMENTS_REQUESTS_BAI_PAGA_NAME)
}

const validateInputs = () => {
    atLeastOneIsEmpty(formData.value, mandatoryList.value) ? showMandatoryErrors.value = true : activeStep.value = 2
}

const payPendingOrder = () => {
    payPendingBaiPagaRequest()
}

/*************************************************
*                                                *
*                    Requests                    *
*                                                *
*************************************************/
const initialRequests = async () => {
    applyErrorState.value = false
	showLoader.value = true

	try {
        await requestAccountsBalances()

        if (getAccountsBalances.value.length && getAccountsBalances.value.find(account => account.id === selectedPayment.value.id) !== undefined) {
            /* Setting Initial originAccount and currency */
            formData.value.originAccount = selectedPayment.value.id
            formData.value.currency = getAccountsBalances.value.find(account => account.id === selectedPayment.value.id).currency
        }

		showLoader.value = false
	} catch (error) {
        applyErrorState.value = true
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, initialRequests)
	}
}

const payPendingBaiPagaRequest = async () => {
    applyErrorState.value = false
	showOverlayLoader.value = true

    const payload = {
        id: selectedPayment.value.id,
        accountId: formData.value.originAccount
    }

	try {
        await payPendingBaiPaga(payload)
        showOverlayLoader.value = false
	} catch (error) {
        if (error.toString() === 'Cancel') return
        errorHandler(error.response.data, payPendingBaiPagaRequest)
	}
}

/*************************************************
*                                                *
*             Success/Error Handlers             *
*                                                *
*************************************************/
const showFeedbackToast = ref(false)
const feedbackToastData = ref(emptyObjectConstructor())
const feedbackOperationData = ref(null)
const responseData = ref()

const successHandler = data => {
    responseData.value = data

    feedbackOperationData.value = {
        status: data.errorLevel,
        title: data.message,
        buttonsData: !data.data.mobilePaymentView.hasReceipt ? null : {
            iconsColor: data.errorLevel === 'INFO' ? 'var(--positive)' : 'var(--warning)',
            emailAction: 'sendMobilePaymentEmailNotification',
            smsAction: 'sendMobilePaymentSmsNotification',
            sendPayload: { operationId: data.data.mobilePaymentView.id },
            downloadUrl: createDownloadMobilePaymentsReceiptPdf(data.data.mobilePaymentView.id),
            operationId: data.data.mobilePaymentView.id
        }
    }

    activeStep.value = 3
}

const errorHandler = (data, retryFn) => {
    showLoader.value = false
    showOverlayLoader.value = false

    feedbackToastData.value = errorObjectConstructor(data, retryFn, () => { activeStep.value = 1 })
	showFeedbackToast.value = true
}

initialRequests()

onBeforeUnmount(() => {
    clearPayFeatureStates()
})
</script>

<style lang="scss" scoped>
</style>
